import { Icon } from "components/icon";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { TierContext } from "tiers/context";
import { Tip } from "view/tooltips";
/** Used for sizing icon buttons. */
const ZERO_WIDTH_SPACE = "\u200B";
/** Custom translation helper. */
const useTt = () => {
    const { t } = useTranslation();
    return function tt(key) {
        // For pre-translated strings, pass a function.
        if (typeof key === "function")
            return key();
        return key ? t(["ui." + key, "props." + key, key]) : undefined;
    };
};
/**
 * A regular button that can optionally display an icon, on the left.
 */
export const Button = (props) => {
    var _a;
    const tiers = React.useContext(TierContext);
    const allowClick = tiers.allow(props) || props.allowWhenTierLocked;
    const tt = useTt();
    const label = tt(props.buttonLabel);
    return React.createElement(Tip, { content: tt(props.hint) },
        React.createElement("button", { className: "ui-button " + ((_a = props.extraClass) !== null && _a !== void 0 ? _a : ""), onClick: allowClick ? props.onClick : () => tiers.popup(label), style: props.widthCss ? { width: props.widthCss } : undefined },
            props.icon ? React.createElement(Icon, { name: props.icon, spacing: true }) : null,
            label,
            tiers.badge(props, "ml-1")));
};
/**
 * A button that displays an icon on the top, with text below.
 */
export const StackButton = (props) => {
    var _a;
    const tiers = React.useContext(TierContext);
    const allowClick = tiers.allow(props) || props.allowWhenTierLocked;
    const tt = useTt();
    const label = tt(props.buttonLabel);
    return React.createElement(Tip, { content: tt(props.hint) },
        React.createElement("button", { className: "ui-button ui-stack-button " + ((_a = props.extraClass) !== null && _a !== void 0 ? _a : ""), style: { width: props.widthCss }, onClick: allowClick ? props.onClick : () => tiers.popup(label) },
            React.createElement(Icon, { name: props.icon, extraClass: "my-1 fa-2x", spacing: false }),
            React.createElement("br", null),
            label,
            tiers.badge(props)));
};
/**
 * A button that consists of an icon only.
 */
export const IconButton = (props) => {
    var _a;
    const tiers = React.useContext(TierContext);
    const allowClick = tiers.allow(props) || props.allowWhenTierLocked;
    const tt = useTt();
    const label = tt(props.buttonLabel);
    return React.createElement(Tip, { content: props.hint ? React.createElement(React.Fragment, null,
            React.createElement("b", null, label),
            " - ",
            tt(props.hint)) : label },
        React.createElement("button", { className: "ui-button " + ((_a = props.extraClass) !== null && _a !== void 0 ? _a : ""), onClick: allowClick ? props.onClick : () => tiers.popup(label), style: { width: props.widthCss } },
            React.createElement("span", { className: "fs-16" },
                React.createElement(Icon, { name: props.icon, spacing: false })),
            ZERO_WIDTH_SPACE,
            tiers.badge(props, "ml-1")));
};
/**
 * Horizontal row of {@link IconButton}s.
 */
export const HorizontalIconButtons = (props) => {
    let items = props.buttons.length;
    let widthCss = (100 / items).round(3) + "%";
    return React.createElement("div", { className: "horizontal-button-group" }, props.buttons.map((b, i) => React.createElement(IconButton, Object.assign({ key: i, widthCss: widthCss }, b))));
};
/**
 * Horizontal row of button, that behave like a toggle, i.e. exactly one item is active.
 * Various sizes are supported.
 */
export const ToggleHorizontalButtons = (props) => {
    var _a;
    let items = (_a = props.buttonsPerRow) !== null && _a !== void 0 ? _a : props.buttons.length;
    let widthCss = (100 / items).round(3) + "%";
    switch (props.size) {
        case "icon":
            return React.createElement("div", { className: "horizontal-toggle-group" }, props.buttons.map((b, i) => React.createElement(IconButton, Object.assign({ key: i, widthCss: widthCss, extraClass: i == props.highlightIndex ? "highlight" : undefined, allowWhenTierLocked: props.allowSelectingTierLockedOptions }, b))));
        case "large":
            return React.createElement("div", { className: "horizontal-toggle-group" }, props.buttons.map((b, i) => React.createElement(StackButton, Object.assign({ key: i, widthCss: widthCss, extraClass: i == props.highlightIndex ? "highlight" : undefined, allowWhenTierLocked: props.allowSelectingTierLockedOptions }, b))));
        case "text":
        case "small":
            return React.createElement("div", { className: "horizontal-toggle-group" }, props.buttons.map((b, i) => React.createElement(Button, Object.assign({ key: i, widthCss: widthCss, extraClass: i == props.highlightIndex ? "highlight" : undefined, allowWhenTierLocked: props.allowSelectingTierLockedOptions }, b, { icon: props.size == "small" ? b.icon : undefined, hint: b.hint }))));
    }
};
/**
 * Vertically arranged {@link Buttons}s.
 */
export const VerticalButtons = (props) => (React.createElement("div", { className: "verticalButtonGroup" }, props.buttons.map((b, i) => React.createElement(Button, Object.assign({ key: i }, b)))));
/**
 * Horizontal buttons where 1 item is highlighted. Basically behaves
 * like a radio button group but using buttons.
 */
export class HorizontalButtonsSwitcher extends React.Component {
    constructor(props) {
        super(props);
        this.state = { value: props.defaultValue };
    }
    render() {
        var _a;
        return React.createElement("div", { className: "horizontal-toggle-group " + ((_a = this.props.className) !== null && _a !== void 0 ? _a : "") }, this.props.options.map((o) => {
            var _a;
            return React.createElement(Button, { key: o.value, icon: o.icon, onClick: () => { this.setState({ value: o.value }); this.props.onSwitch(o.value); }, extraClass: ((_a = this.props.extraClass) !== null && _a !== void 0 ? _a : "") + (this.state.value == o.value ? " highlight" : ""), buttonLabel: o.buttonLabel });
        }));
    }
}
